/**
 * POSTs to an inbox route relative to the page origin so cookies and the
 * POST CSRF origin check stay aligned. `baseUrl()` is always a
 * `http://localhost` URL in dev, which 403s when the inbox is opened on
 * 127.0.0.1 or a LAN host. The installation scope is left off for every
 * installation.
 *
 * An expired session rejects with a 401 error, which the global query error
 * handler turns into a sign-in redirect. Inbox queries persist to storage that
 * every window shares, so an auth failure resolved as data would outlive the
 * session and send the next signed-in inbox load back to sign-in.
 */
export async function fetchInboxV2<T>(params: {
  path: string;
  installationId: number | null | undefined;
  /** The route's own query parameters, e.g. `{ section: 'open' }`. */
  query: Record<string, string>;
  signal: AbortSignal;
  /** Names what was requested in the error, e.g. "open check summaries". */
  description: string;
}): Promise<T> {
  const query = new URLSearchParams(params.query);
  if (params.installationId !== null && params.installationId !== undefined) {
    query.set('installationId', params.installationId.toString());
  }
  const queryString = query.toString();

  const response = await fetch(queryString ? `${params.path}?${queryString}` : params.path, {
    credentials: 'include',
    method: 'POST',
    signal: params.signal,
  });

  if (!response.ok) {
    if (response.status === 401) {
      const errorData = (await response.json()) as { error?: string };
      if (errorData.error) {
        throw Object.assign(new Error(errorData.error), { status: 401 });
      }
    }

    throw new Error(`Failed to fetch inbox v2 ${params.description}`);
  }

  return (await response.json()) as T;
}
